import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Filter, MoreVertical, Shield, ShieldOff, CreditCard, Plus, UserPlus, X, TrendingUp, TrendingDown, Wallet } from 'lucide-react';
import { config } from '../../../config';

interface Plan {
    id: number;
    name: string;
    maxUsers: number;
    maxSectors: number;
    maxConnections: number;
}

interface User {
    id: number;
    name: string;
    email: string;
    status: boolean;
    role: string;
    createdAt: string;
    subscriptionId: number;
    planId: number;
    planName: string;
    totalSectors: number;
    totalInvitations: number;
    invitedBy?: number | null;
    invitedByName?: string | null;
    financeiroAccessEnabled?: boolean;
    financeiroExternalRef?: string | null;
    canAccessFinanceiro?: boolean;
    financeiroOwnWorkspace?: boolean;
    financeiroCanCreate?: boolean;
    financeiroCanEdit?: boolean;
    financeiroCanDelete?: boolean;
}

const AdminUsers: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [convidadoPorFilter, setConvidadoPorFilter] = useState<'all' | 'invited' | 'not_invited'>('all');

    // Modals state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [newUserData, setNewUserData] = useState({ name: '', email: '', phone: '', planId: 0 });
    const [selectedPlanId, setSelectedPlanId] = useState(0);
    const [modalStatus, setModalStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPromoteModal, setShowPromoteModal] = useState<User | null>(null);
    const [promotingUserId, setPromotingUserId] = useState<number | null>(null);
    const [showDemoteModal, setShowDemoteModal] = useState<User | null>(null);
    const [demotingUserId, setDemotingUserId] = useState<number | null>(null);

    const [showFinanceiroModal, setShowFinanceiroModal] = useState<User | null>(null);
    const [finEnabled, setFinEnabled] = useState(false);
    const [finOwnWorkspace, setFinOwnWorkspace] = useState(false);
    const [finCanCreate, setFinCanCreate] = useState(true);
    const [finCanEdit, setFinCanEdit] = useState(true);
    const [finCanDelete, setFinCanDelete] = useState(true);
    const [financeiroSaving, setFinanceiroSaving] = useState(false);

    useEffect(() => {
        fetchUsers();
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/platform/plans`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            // Remover duplicatas de planos por nome (segurança extra front-end)
            const uniquePlans: Plan[] = [];
            const seenNames = new Set();
            response.data.forEach((p: Plan) => {
                if (!seenNames.has(p.name)) {
                    seenNames.add(p.name);
                    uniquePlans.push(p);
                }
            });

            setPlans(uniquePlans);
            if (uniquePlans.length > 0) {
                setNewUserData(prev => ({ ...prev, planId: uniquePlans[0].id }));
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${config.API_BASE_URL}/platform/users`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setModalStatus({ type: null, message: '' });
        try {
            await axios.post(`${config.API_BASE_URL}/platform/users`, newUserData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setModalStatus({ type: 'success', message: 'Usuário administrador criado com sucesso!' });
            setNewUserData({ name: '', email: '', phone: '', planId: plans[0]?.id || 0 });

            // Refresh users list immediately
            fetchUsers();

            // Close after 2 seconds
            setTimeout(() => {
                setIsAddModalOpen(false);
                setModalStatus({ type: null, message: '' });
            }, 2000);
        } catch (error: any) {
            console.error('Error adding user:', error);
            setModalStatus({ type: 'error', message: error.response?.data?.message || 'Erro ao criar usuário' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChangePlan = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return;
        setIsSubmitting(true);
        setModalStatus({ type: null, message: '' });
        try {
            await axios.put(`${config.API_BASE_URL}/platform/users/${selectedUser.id}/plan`,
                selectedPlanId,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setModalStatus({ type: 'success', message: 'Plano atualizado com sucesso!' });

            // Instant local update for the user in the list
            const updatedPlan = plans.find(p => p.id === selectedPlanId);
            setUsers(users.map(u => u.id === selectedUser.id ? {
                ...u,
                planName: updatedPlan?.name || 'N/A',
                planId: selectedPlanId
            } : u));

            // Silently refresh in background
            fetchUsers();

            setTimeout(() => {
                setIsPlanModalOpen(false);
                setModalStatus({ type: null, message: '' });
            }, 2000);
        } catch (error: any) {
            console.error('Error updating plan:', error);
            setModalStatus({ type: 'error', message: error.response?.data?.message || 'Erro ao atualizar plano' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePromoteToSuperAdmin = async (user: User) => {
        setPromotingUserId(user.id);
        setModalStatus({ type: null, message: '' });
        try {
            await axios.post(`${config.API_BASE_URL}/users/${user.id}/promote-to-superadmin`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setModalStatus({ type: 'success', message: `${user.name} foi promovido a SuperAdmin com sucesso!` });
            fetchUsers();
            setTimeout(() => {
                setShowPromoteModal(null);
                setModalStatus({ type: null, message: '' });
            }, 2000);
        } catch (error: any) {
            setModalStatus({ type: 'error', message: error.response?.data?.message || 'Erro ao promover usuário' });
        } finally {
            setPromotingUserId(null);
        }
    };

    const handleDemoteFromSuperAdmin = async (user: User) => {
        setDemotingUserId(user.id);
        setModalStatus({ type: null, message: '' });
        try {
            await axios.post(`${config.API_BASE_URL}/users/${user.id}/demote-from-superadmin`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setModalStatus({ type: 'success', message: `${user.name} foi despromovido a Administrador com sucesso!` });
            fetchUsers();
            setTimeout(() => {
                setShowDemoteModal(null);
                setModalStatus({ type: null, message: '' });
            }, 2000);
        } catch (error: any) {
            setModalStatus({ type: 'error', message: error.response?.data?.message || 'Erro ao despromover usuário' });
        } finally {
            setDemotingUserId(null);
        }
    };

    const openFinanceiroModal = (user: User) => {
        setShowFinanceiroModal(user);
        setFinEnabled(Boolean(user.financeiroAccessEnabled));
        setFinOwnWorkspace(Boolean(user.financeiroOwnWorkspace));
        setFinCanCreate(user.financeiroCanCreate ?? true);
        setFinCanEdit(user.financeiroCanEdit ?? true);
        setFinCanDelete(user.financeiroCanDelete ?? true);
        setModalStatus({ type: null, message: '' });
    };

    const isPlatformAdminRow = (user: User) => user.role === 'PlatformAdmin';
    const isSuperAdminRow = (user: User) => user.role === 'SuperAdmin';

    const handleSaveFinanceiro = async () => {
        const user = showFinanceiroModal;
        if (!user || isPlatformAdminRow(user)) return;
        setFinanceiroSaving(true);
        setModalStatus({ type: null, message: '' });
        try {
            // SuperAdmin sempre opera no compartilhado e tem CRUD completo (backend ignora flags).
            const isSA = isSuperAdminRow(user);
            await axios.put(
                `${config.API_BASE_URL}/platform/users/${user.id}/financeiro-access`,
                {
                    enabled: finEnabled,
                    setExternalRef: false,
                    ownWorkspace: isSA ? false : finOwnWorkspace,
                    canCreate: isSA ? true : finCanCreate,
                    canEdit: isSA ? true : finCanEdit,
                    canDelete: isSA ? true : finCanDelete,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            setModalStatus({ type: 'success', message: 'Módulo Financeiro atualizado.' });
            fetchUsers();
            setTimeout(() => {
                setShowFinanceiroModal(null);
                setModalStatus({ type: null, message: '' });
            }, 1500);
        } catch (error: any) {
            const msg = error.response?.data?.message ?? error.response?.data?.Message;
            setModalStatus({
                type: 'error',
                message: (typeof msg === 'string' && msg) || 'Erro ao salvar Financeiro.',
            });
        } finally {
            setFinanceiroSaving(false);
        }
    };

    const toggleStatus = async (user: User) => {
        try {
            const newStatus = !user.status;
            await axios.put(`${config.API_BASE_URL}/platform/users/${user.id}/status`,
                newStatus,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Local state update
            setUsers(users.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
        } catch (error: any) {
            console.error('Error toggling status:', error);
            alert(error.response?.data?.message || 'Erro ao alterar status do usuário');
        }
    };

    const filteredUsers = users.filter(user => {
        const matchSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.invitedByName || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchConvidadoPor = convidadoPorFilter === 'all' ||
            (convidadoPorFilter === 'invited' && user.invitedBy != null) ||
            (convidadoPorFilter === 'not_invited' && user.invitedBy == null);
        return matchSearch && matchConvidadoPor;
    });

    return (
        <div className="p-1">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 font-display">Gerenciar Usuários (Tenants)</h1>

                <div className="flex space-x-3">
                    <select
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        value={convidadoPorFilter}
                        onChange={(e) => setConvidadoPorFilter(e.target.value as 'all' | 'invited' | 'not_invited')}
                    >
                        <option value="all">Convidado por: Todos</option>
                        <option value="invited">Convidados</option>
                        <option value="not_invited">Não convidados</option>
                    </select>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar usuário..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-sm font-medium"
                    >
                        <UserPlus size={20} className="mr-2" />
                        Novo Usuário
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Convidado por</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Info</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Financeiro</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cadastrado</th>
                            <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                    <div className="flex flex-col items-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                                        Carregando usuários...
                                    </div>
                                </td>
                            </tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-12 text-center text-gray-400 italic">
                                    Nenhum usuário encontrado.
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mr-3 shadow-sm border border-blue-200">
                                                {user.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">{user.name}</div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                                <div className="flex space-x-2 mt-1">
                                                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded uppercase font-bold tracking-wider border border-gray-200">
                                                        {user.role}
                                                    </span>
                                                    {user.planName !== "N/A" && (
                                                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded uppercase font-bold tracking-wider border border-blue-100">
                                                            Plan: {user.planName}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 inline-flex text-[10px] leading-5 font-bold uppercase rounded-full tracking-wide shadow-sm ${user.status ? 'bg-[#0C85BA]/15 text-[#0C85BA] border border-[#0C85BA]/30' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                                            {user.status ? 'Ativo' : 'Bloqueado'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm text-gray-600">
                                            {user.invitedByName || '—'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-xs text-gray-600 space-y-1">
                                            <div className="flex items-center">
                                                <span className="w-16 font-medium text-gray-400">Setores:</span>
                                                <span className="bg-gray-50 px-1.5 py-0.5 rounded text-blue-600 font-bold border border-gray-100">{user.totalSectors}</span>
                                            </div>
                                            <div className="flex items-center">
                                                <span className="w-16 font-medium text-gray-400">Convites:</span>
                                                <span className="bg-gray-50 px-1.5 py-0.5 rounded text-indigo-600 font-bold border border-gray-100">{user.totalInvitations}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {isPlatformAdminRow(user) ? (
                                            <span className="text-sm text-gray-400">—</span>
                                        ) : (
                                            <div className="flex flex-col gap-1">
                                                <span
                                                    className={`px-2 py-0.5 inline-flex text-[10px] leading-5 font-bold uppercase rounded-full tracking-wide border w-fit ${
                                                        user.canAccessFinanceiro
                                                            ? 'bg-[#0C85BA]/10 text-[#0C85BA] border-[#0C85BA]/30'
                                                            : 'bg-gray-100 text-gray-600 border-gray-200'
                                                    }`}
                                                >
                                                    {user.canAccessFinanceiro ? 'Liberado' : 'Desativado'}
                                                </span>
                                                {user.canAccessFinanceiro && (
                                                    <span className="text-[10px] text-gray-500 font-medium">
                                                        {user.financeiroOwnWorkspace
                                                            ? 'Workspace próprio'
                                                            : `Compartilhado · ${
                                                                  user.role === 'SuperAdmin'
                                                                      ? 'CRUD total'
                                                                      : [
                                                                            user.financeiroCanCreate ? 'Criar' : null,
                                                                            user.financeiroCanEdit ? 'Editar' : null,
                                                                            user.financeiroCanDelete ? 'Deletar' : null,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(' · ') || 'Somente leitura'
                                                              }`}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {new Date(user.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end space-x-1">
                                            {user.role !== 'SuperAdmin' && (
                                                <button
                                                    onClick={() => setShowPromoteModal(user)}
                                                    className="p-2 text-[#0C85BA] hover:bg-[#0C85BA]/10 rounded-lg transition-all"
                                                    title="Promover a SuperAdmin"
                                                >
                                                    <TrendingUp size={18} />
                                                </button>
                                            )}
                                            {user.role === 'SuperAdmin' && (
                                                <button
                                                    onClick={() => { setShowDemoteModal(user); setModalStatus({ type: null, message: '' }); }}
                                                    className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                                                    title="Despromover a Administrador"
                                                >
                                                    <TrendingDown size={18} />
                                                </button>
                                            )}
                                            {!isPlatformAdminRow(user) && (
                                                <button
                                                    onClick={() => openFinanceiroModal(user)}
                                                    className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-all"
                                                    title="Módulo Financeiro"
                                                >
                                                    <Wallet size={18} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => toggleStatus(user)}
                                                className={`p-2 rounded-lg transition-all ${user.status ? 'text-red-500 hover:bg-red-50' : 'text-[#0C85BA] hover:bg-[#0C85BA]/10'}`}
                                                title={user.status ? "Bloquear" : "Ativar"}
                                            >
                                                {user.status ? <ShieldOff size={18} /> : <Shield size={18} />}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    setSelectedPlanId(user.planId || 0);
                                                    setIsPlanModalOpen(true);
                                                }}
                                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                                                title="Alterar Plano"
                                            >
                                                <CreditCard size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal: Adicionar Usuário */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Novo Usuário Admin</h2>
                            <button onClick={() => { setIsAddModalOpen(false); setModalStatus({ type: null, message: '' }); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {modalStatus.type === 'success' ? (
                            <div className="flex flex-col items-center justify-center py-8 animate-in zoom-in-95 duration-300">
                                <div className="h-20 w-20 bg-[#0C85BA]/15 text-[#0C85BA] rounded-full flex items-center justify-center mb-4 shadow-inner">
                                    <Plus className="rotate-45 scale-150" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Sucesso!</h3>
                                <p className="text-gray-500 text-center">{modalStatus.message}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleAddUser} className="space-y-5">
                                {modalStatus.type === 'error' && (
                                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
                                        {modalStatus.message}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nome da Empresa/Admin</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        placeholder="Ex: Minha Empresa"
                                        value={newUserData.name}
                                        onChange={e => setNewUserData({ ...newUserData, name: e.target.value })}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
                                    <input
                                        type="email"
                                        required
                                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        placeholder="email@empresa.com"
                                        value={newUserData.email}
                                        onChange={e => setNewUserData({ ...newUserData, email: e.target.value })}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">WhatsApp</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                                        placeholder="55119..."
                                        value={newUserData.phone}
                                        onChange={e => setNewUserData({ ...newUserData, phone: e.target.value })}
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Plano Inicial</label>
                                    <select
                                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all appearance-none bg-no-repeat bg-[right_1rem_center]"
                                        value={newUserData.planId}
                                        onChange={e => setNewUserData({ ...newUserData, planId: parseInt(e.target.value) })}
                                        disabled={isSubmitting}
                                    >
                                        {plans.map(plan => (
                                            <option key={plan.id} value={plan.id}>{plan.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setIsAddModalOpen(false)}
                                        className="flex-1 px-4 py-3 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors font-semibold disabled:opacity-50"
                                        disabled={isSubmitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 font-semibold disabled:opacity-50 flex items-center justify-center"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        ) : 'Criar Usuário'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Modal: Promover a SuperAdmin */}
            {showPromoteModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Promover a SuperAdmin</h2>
                            <button onClick={() => { setShowPromoteModal(null); setModalStatus({ type: null, message: '' }); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {modalStatus.type === 'success' ? (
                            <div className="flex flex-col items-center justify-center py-8 animate-in zoom-in-95 duration-300">
                                <div className="h-20 w-20 bg-[#0C85BA]/15 text-[#0C85BA] rounded-full flex items-center justify-center mb-4 shadow-inner">
                                    <TrendingUp className="scale-150" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Promovido!</h3>
                                <p className="text-gray-500 text-center">{modalStatus.message}</p>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center mb-6">
                                    <div className="h-12 w-12 rounded-full bg-[#0C85BA]/15 text-[#0C85BA] flex items-center justify-center font-bold mr-3">
                                        {showPromoteModal.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{showPromoteModal.name}</div>
                                        <div className="text-sm text-gray-500">{showPromoteModal.email}</div>
                                    </div>
                                </div>
                                <p className="text-gray-600 mb-6">
                                    Ao promover este usuário a SuperAdmin, ele terá sua própria assinatura e poderá criar setores e convidar outros usuários.
                                </p>
                                {modalStatus.type === 'error' && (
                                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100 mb-6">
                                        {modalStatus.message}
                                    </div>
                                )}
                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => { setShowPromoteModal(null); setModalStatus({ type: null, message: '' }); }}
                                        className="flex-1 px-4 py-3 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors font-semibold disabled:opacity-50"
                                        disabled={!!promotingUserId}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handlePromoteToSuperAdmin(showPromoteModal)}
                                        className="flex-1 px-4 py-3 bg-[#0C85BA] text-white rounded-xl hover:bg-[#0C85BA]/85 transition-colors shadow-lg shadow-[#0C85BA]/30 font-semibold disabled:opacity-50 flex items-center justify-center"
                                        disabled={!!promotingUserId}
                                    >
                                        {promotingUserId ? (
                                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        ) : (
                                            <>Confirmar promoção</>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal: Despromover de SuperAdmin */}
            {showDemoteModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Despromover a Administrador</h2>
                            <button onClick={() => { setShowDemoteModal(null); setModalStatus({ type: null, message: '' }); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {modalStatus.type === 'success' ? (
                            <div className="flex flex-col items-center justify-center py-8 animate-in zoom-in-95 duration-300">
                                <div className="h-20 w-20 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                                    <TrendingDown className="scale-150" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Despromovido!</h3>
                                <p className="text-gray-500 text-center">{modalStatus.message}</p>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center mb-6">
                                    <div className="h-12 w-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold mr-3">
                                        {showDemoteModal.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{showDemoteModal.name}</div>
                                        <div className="text-sm text-gray-500">{showDemoteModal.email}</div>
                                    </div>
                                </div>
                                <p className="text-gray-600 mb-6">
                                    Ao despromover este usuário, ele deixará de ser SuperAdmin e passará a ser Administrador. Ele perderá sua assinatura e poderá ser convidado para setores por outros SuperAdmins.
                                </p>
                                {modalStatus.type === 'error' && (
                                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100 mb-6">
                                        {modalStatus.message}
                                    </div>
                                )}
                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => { setShowDemoteModal(null); setModalStatus({ type: null, message: '' }); }}
                                        className="flex-1 px-4 py-3 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors font-semibold disabled:opacity-50"
                                        disabled={!!demotingUserId}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDemoteFromSuperAdmin(showDemoteModal)}
                                        className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors shadow-lg shadow-amber-200 font-semibold disabled:opacity-50 flex items-center justify-center"
                                        disabled={!!demotingUserId}
                                    >
                                        {demotingUserId ? (
                                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        ) : (
                                            <>Confirmar despromoção</>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal: Módulo Financeiro (Platform Admin) */}
            {showFinanceiroModal && !isPlatformAdminRow(showFinanceiroModal) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Módulo Financeiro</h2>
                            <button
                                onClick={() => {
                                    setShowFinanceiroModal(null);
                                    setModalStatus({ type: null, message: '' });
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {modalStatus.type === 'success' ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <div className="h-20 w-20 bg-violet-100 text-violet-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                                    <Wallet className="scale-150" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Salvo</h3>
                                <p className="text-gray-500 text-center">{modalStatus.message}</p>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center mb-4">
                                    <div className="h-12 w-12 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold mr-3">
                                        {showFinanceiroModal.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{showFinanceiroModal.name}</div>
                                        <div className="text-sm text-gray-500">{showFinanceiroModal.email}</div>
                                    </div>
                                </div>

                                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                                    Quem estiver <strong>liberado</strong> pode abrir o módulo em{' '}
                                    <code className="text-xs bg-gray-100 px-1 rounded">/financeiro</code> no mesmo domínio.
                                </p>
                                <label className="flex items-center gap-3 cursor-pointer mb-4 p-4 rounded-xl border-2 border-gray-100 hover:border-violet-200 transition-colors">
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5 accent-violet-600 rounded"
                                        checked={finEnabled}
                                        onChange={(e) => setFinEnabled(e.target.checked)}
                                        disabled={financeiroSaving}
                                    />
                                    <span className="text-sm font-medium text-gray-800">Liberar módulo Financeiro para este usuário</span>
                                </label>

                                {finEnabled && !isSuperAdminRow(showFinanceiroModal) && (
                                    <div className="space-y-4 mb-4">
                                        <div className="p-4 rounded-xl border-2 border-gray-100">
                                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                                                Workspace
                                            </div>
                                            <div className="space-y-2">
                                                <label className="flex items-start gap-3 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="finWorkspace"
                                                        className="mt-1 accent-violet-600"
                                                        checked={!finOwnWorkspace}
                                                        onChange={() => setFinOwnWorkspace(false)}
                                                        disabled={financeiroSaving}
                                                    />
                                                    <div>
                                                        <div className="text-sm font-semibold text-gray-800">Compartilhado (padrão)</div>
                                                        <div className="text-xs text-gray-500">
                                                            Vê o mesmo Financeiro do tenant. Permissões CRUD abaixo controlam o que pode fazer.
                                                        </div>
                                                    </div>
                                                </label>
                                                <label className="flex items-start gap-3 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="finWorkspace"
                                                        className="mt-1 accent-violet-600"
                                                        checked={finOwnWorkspace}
                                                        onChange={() => setFinOwnWorkspace(true)}
                                                        disabled={financeiroSaving}
                                                    />
                                                    <div>
                                                        <div className="text-sm font-semibold text-gray-800">Próprio (isolado)</div>
                                                        <div className="text-xs text-gray-500">
                                                            Workspace privado. Dados não são vistos por mais ninguém — nem pelo SuperAdmin do tenant.
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        <div
                                            className={`p-4 rounded-xl border-2 ${finOwnWorkspace ? 'border-gray-100 bg-gray-50/50' : 'border-gray-100'}`}
                                        >
                                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                                                Permissões {finOwnWorkspace && <span className="text-gray-400 normal-case font-normal">(workspace próprio: dono pode tudo)</span>}
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 accent-violet-600"
                                                        checked={finOwnWorkspace ? true : finCanCreate}
                                                        onChange={(e) => setFinCanCreate(e.target.checked)}
                                                        disabled={financeiroSaving || finOwnWorkspace}
                                                    />
                                                    <span className="text-gray-700 font-medium">Criar</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 accent-violet-600"
                                                        checked={finOwnWorkspace ? true : finCanEdit}
                                                        onChange={(e) => setFinCanEdit(e.target.checked)}
                                                        disabled={financeiroSaving || finOwnWorkspace}
                                                    />
                                                    <span className="text-gray-700 font-medium">Editar</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 accent-violet-600"
                                                        checked={finOwnWorkspace ? true : finCanDelete}
                                                        onChange={(e) => setFinCanDelete(e.target.checked)}
                                                        disabled={financeiroSaving || finOwnWorkspace}
                                                    />
                                                    <span className="text-gray-700 font-medium">Deletar</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {finEnabled && isSuperAdminRow(showFinanceiroModal) && (
                                    <div className="mb-4 p-3 rounded-lg bg-violet-50 border border-violet-100 text-xs text-violet-700">
                                        SuperAdmin do tenant sempre opera no Financeiro compartilhado com permissões totais.
                                    </div>
                                )}

                                {modalStatus.type === 'error' && (
                                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100 mb-4">
                                        {modalStatus.message}
                                    </div>
                                )}

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowFinanceiroModal(null);
                                            setModalStatus({ type: null, message: '' });
                                        }}
                                        className="flex-1 px-4 py-3 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors font-semibold"
                                        disabled={financeiroSaving}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveFinanceiro}
                                        className="flex-1 px-4 py-3 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-colors shadow-lg font-semibold flex items-center justify-center gap-2"
                                        disabled={financeiroSaving}
                                    >
                                        {financeiroSaving ? (
                                            <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            'Salvar'
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal: Alterar Plano */}
            {isPlanModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-md p-8 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Alterar Plano</h2>
                            <button onClick={() => { setIsPlanModalOpen(false); setModalStatus({ type: null, message: '' }); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {modalStatus.type === 'success' ? (
                            <div className="flex flex-col items-center justify-center py-8 animate-in zoom-in-95 duration-300">
                                <div className="h-20 w-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4 shadow-inner">
                                    <CreditCard className="scale-150" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Plano Atualizado!</h3>
                                <p className="text-gray-500 text-center">{modalStatus.message}</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-500 mb-6 font-medium">
                                    Alterando plano de: <span className="text-gray-900 font-bold">{selectedUser?.name}</span>
                                </p>

                                <form onSubmit={handleChangePlan} className="space-y-5">
                                    {modalStatus.type === 'error' && (
                                        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
                                            {modalStatus.message}
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Selecione o novo plano:</label>
                                        <div className="space-y-3">
                                            {plans.map(plan => (
                                                <label
                                                    key={plan.id}
                                                    className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedPlanId === plan.id ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-100' : 'border-gray-100 hover:border-gray-200'} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <div className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="plan"
                                                            className="hidden"
                                                            checked={selectedPlanId === plan.id}
                                                            onChange={() => !isSubmitting && setSelectedPlanId(plan.id)}
                                                            disabled={isSubmitting}
                                                        />
                                                        <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${selectedPlanId === plan.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                                                            {selectedPlanId === plan.id && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                                        </div>
                                                        <div>
                                                            <p className={`font-bold ${selectedPlanId === plan.id ? 'text-blue-700' : 'text-gray-900'}`}>{plan.name}</p>
                                                            <p className="text-xs text-gray-500">{plan.maxUsers} usuários • {plan.maxSectors} setores</p>
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex space-x-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => setIsPlanModalOpen(false)}
                                            className="flex-1 px-4 py-3 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors font-semibold disabled:opacity-50"
                                            disabled={isSubmitting}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 font-semibold disabled:opacity-50 flex items-center justify-center"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            ) : 'Salvar Alteração'}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
